import React, { useState } from "react";

import CheckIcon from "@uui/Check";
import LinkIcon from "@uui/Link01";
import ExternalLinkIcon from "@uui/LinkExternal01";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useSchedulingLinks from "@app/analytics/Calendar/hooks/useSchedulingLinks";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import * as Events from "@app/analytics/tracking/events";
import useTimeout from "@app/common/hooks/useTimeout";
import ROUTES from "@app/common/routes";
import type { SchedulingLink } from "@app/common/types";
import { getSchedulingLinkUrl } from "@app/common/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import CopyButton from "@rui/CopyButton";
import { Link } from "@rui/Link";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import { SideNavItemContainer } from "./styles";

const RSS_LINKS_TOOLTIP =
  "Simplify scheduling with AI. Share this unique link with others to schedule a time to meet based on availability and predicted engagement.";

const LinkContainer = styled(Row)(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.shades.primary[500],
  padding: theme.spacing(1),
  borderRadius: "8px",
  color: "#FFF",
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  minHeight: "36px",
  overflow: "hidden",
  "& .MuiSvgIcon-root": { fontSize: "16px" },
  "&:hover": {
    cursor: "pointer",
    backgroundColor: theme.palette.shades.primary[600],
  },
  "&.copied": {
    backgroundColor: theme.palette.shades.meadow[600],
    "&:hover": {
      backgroundColor: theme.palette.shades.meadow[600],
    },
  },
}));

const ManageButton = styled(Button)(({ theme }) => ({
  color: "#FFF",
  backgroundColor: theme.palette.transparent.white[4],
  height: "100%",
  fontWeight: 400,
  "&:hover": {
    backgroundColor: theme.palette.transparent.white[12],
    color: "#FFF",
  },
}));

const LinkMenu = ({
  expanded,
  links,
  onCopy,
  rootVanityId,
}: {
  expanded: boolean;
  links: SchedulingLink[];
  onCopy: () => void;
  rootVanityId: string;
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  const { anchorElement, onClick, onClose, open } = useAnchorElement();

  useTimeout(() => setLinkCopied(false), 1500, linkCopied);

  const handleMenuItemClick = () => {
    setLinkCopied(true);
    onClose();
  };

  return (
    <>
      <LinkContainer
        variant={expanded ? "apart" : "center"}
        onClick={onClick}
        className={linkCopied ? "copied" : undefined}
      >
        {expanded && (
          <Typography variant="bodySecondaryReg">
            {linkCopied ? "Copied!" : "Copy link"}
          </Typography>
        )}
        {linkCopied ? <CheckIcon /> : <LinkIcon />}
      </LinkContainer>

      <Menu anchorEl={anchorElement} open={open} onClose={onClose}>
        <CopyButton
          onClick={onCopy}
          copyValue={getSchedulingLinkUrl(rootVanityId)}
          renderButton={({ onClick }) => (
            <MenuItem
              onClick={(event) => {
                onClick(event);
                handleMenuItemClick();
              }}
            >
              Any duration
            </MenuItem>
          )}
        />
        {links.map((link) => {
          if (!link.isEnabled) return null;
          return (
            <CopyButton
              key={link.id}
              onClick={onCopy}
              copyValue={getSchedulingLinkUrl(rootVanityId, link.vanityId)}
              renderButton={({ onClick }) => (
                <MenuItem
                  onClick={(event) => {
                    onClick(event);
                    handleMenuItemClick();
                  }}
                >
                  {link.allowedDuration}-minute
                </MenuItem>
              )}
            />
          );
        })}
      </Menu>
    </>
  );
};

const SchedulingLink = ({ expanded }: { expanded: boolean }) => {
  const navigate = useNavigate();
  const { userMetadata } = useUserSettings();
  const { rssLinkSettings, rootSchedulingLinkUrl } = useRssLinksSettings();

  const { defaultLinks } = useSchedulingLinks({
    enabled: rssLinkSettings.isEnabled,
  });

  const handleCopy = () => {
    Events.copySchedulingLink({ source: "side_nav" });
    if (!userMetadata.smartSchedulerTutorialComplete) {
      navigate({ search: "popup=rss_tutorial" }, { replace: true });
    }
  };

  return (
    <SideNavItemContainer spacing={1} expanded={expanded}>
      {expanded && (
        <Row variant="left" spacing={1}>
          <Typography variant="bodySecondaryReg" sx={{ whiteSpace: "nowrap" }}>
            Smart Scheduler Link
          </Typography>
          <TooltipIcon title={RSS_LINKS_TOOLTIP} />
        </Row>
      )}
      <Row spacing={1}>
        {rssLinkSettings.isEnabled ? (
          <LinkMenu
            expanded={expanded}
            links={defaultLinks}
            onCopy={handleCopy}
            rootVanityId={rssLinkSettings.vanityId}
          />
        ) : (
          <Link href={rootSchedulingLinkUrl} sx={{ width: "100%" }}>
            <LinkContainer variant={expanded ? "apart" : "center"}>
              {expanded && (
                <Typography variant="bodySecondaryReg">Open link</Typography>
              )}
              {expanded ? <ExternalLinkIcon /> : <LinkIcon />}
            </LinkContainer>
          </Link>
        )}
        {expanded && (
          <Link to={ROUTES.CALENDAR_SCHEDULING}>
            <ManageButton>Manage</ManageButton>
          </Link>
        )}
      </Row>
    </SideNavItemContainer>
  );
};

export default SchedulingLink;
